import i18n from '@/locales/i18n';
import SocketService from '@/services/socket.service';
import DisableArcadeMixin from '@/mixins/DisableArcade.mixin';
import { MODES, TAI } from '@/app.config';
import { UnitFilter } from '@/filters/Temp.filter';
import { mapGetters } from 'vuex';

const Unit = {
  mixins: [DisableArcadeMixin],
  filters: { UnitFilter },
  computed: {
    ...mapGetters({
      getDevice: 'unitsModule/getDevice',
    }),

    /**
     * Devuelve los datos del dispositivo almacenados en el Store
     *
     * @return {Device} - Datos del dispositivo
     */
    device() {
      return this.getDevice(this.installationID, this.mac);
    },

    /**
     * Cuando este componente se comporta como ACS, implica algunos cambios en su visualización,
     * para poder manejarlos, generamos esta propiedad
     *
     * @return {Boolean} - Componente del tipo ACS
     */
    ACS() {
      return this.device.acs_available;
    },

    /**
     * Dependiendo del dispositivo altherma, debemos utilizar los parámetros de aire o agua,
     * esta propiedad devuelve si hay que utilizar el de agua o no.
     */
    isWater() {
      return this.device.acs_available && this.device.tai_th === TAI.WATER;
    },

    /**
     * Al loguearse un usuario, recibe unos datos mínimos de cada dispositivo,
     * pero la mayoría de los datos los recibe tras conectarse a los sockets,
     * esta propiedad indica si estos datos se han recibido o no (loading)
     *
     * @return {Boolean} - Indica si se han recibido los datos o no
     */
    loading() {
      if (!this.device) return true;
      return !Object.prototype.hasOwnProperty.call(this.device, 'isConnected');
    },

    /**
     * Devuelve la descripción con el error de la unidad
     * o un valor nulo cuando no hay ninguno
     *
     * @return {String} - Descrición del error
     */
    deviceFail() {
      if (!this.device) return null;

      if (!this.device.isConnected) {
        return i18n.t('home.notConnected');
      }

      if (this.device.isConnected && !this.device.machineready && !this.device.error_value) {
        return i18n.t('home.machineNotReady');
      }

      if (this.device.isConnected && this.device.error_value) {
        return i18n.t('home.machineError');
      }

      return null;
    },

    /**
     * Devuelve si el calor de emergencia está activado
     */
    emergencyHeatActivated() {
      return this.device.emerheatpresent && this.device.emerheatstatus;
    },

    /**
     * Devuelve si la entrada digital está activada
     */
    digitalInputActivated() {
      return this.device.t1t2on ? i18n.t('digitalInput.activated') : false;
    },

    /**
     * Devuelve la unidad de temperatura
     */
    tempUnit() {
      return this.$options.filters.UnitFilter(this.device.units);
    },

    /**
     * Devuelve la temperatura local
     *
     * Si el dispositivo tienen algún error, no devuelve un valor
     * La temperatura local puede venir por dos parámetros work_temp o local_temp
     *
     * @return {Number} - Temperatura local
     */
    localTemp() {
      if (this.deviceFail) return '--';
      return this.device.work_temp || this.device.local_temp;
    },

    /**
     * Indica si el dispositivo está encendido o no
     */
    power() {
      if (this.ACSMode) {
        return this.device.power_acs;
      }
      return this.device.power;
    },

    /**
     * Indica si la unidad tiene alguna función bloqueada
     *
     * @return {Boolean} - Indica si la unidad está bloqueada
     */
    blockDevice() {
      return (
        this.device.block_on ||
        this.device.block_off ||
        this.device.block_mode ||
        this.device.block_setpoint
      );
    },

    fallback() {
      return this.device && this.device.link_fallback && !this.device.link_isConnected;
    },

    /**
     * Devuelve el valor de la temperatura seleccionada en el dispositivo
     *
     * @return {Number} - Temperatura seleccinada
     */
    getSetPoint() {
      if (this.ACS && this.ACSMode) return this.device.setpoint_acs;

      let setPoint = 0;

      switch (this.device.mode) {
        case MODES.AUTO:
          setPoint = this.isWater ? this.device.setpoint_water_auto : this.device.setpoint_air_auto;
          break;
        case MODES.COOLING:
          setPoint = this.isWater ? this.device.setpoint_water_cool : this.device.setpoint_air_cool;
          break;
        case MODES.HEATING:
          setPoint = this.isWater ? this.device.setpoint_water_heat : this.device.setpoint_air_heat;
          break;
        default:
          setPoint = this.isWater
            ? Math.min(this.device.range_sp_cool_water_max, this.device.range_sp_hot_water_max)
            : Math.min(this.device.range_sp_cool_air_max, this.device.range_sp_hot_air_max);
      }

      return setPoint;
    },

    /**
     * Devuelve el valor máximo de temperatura del dispositivo
     *
     * @return {Number} - Temperatura máxima
     */
    maxTemp() {
      if (this.ACS && this.ACSMode) return this.device.range_sp_acs_max;

      let maxTemp = 0;

      switch (this.device.mode) {
        case MODES.AUTO:
          maxTemp = this.isWater
            ? this.device.range_sp_auto_water_max
            : this.device.range_sp_auto_air_max;
          break;
        case MODES.COOLING:
          maxTemp = this.isWater
            ? this.device.range_sp_cool_water_max
            : this.device.range_sp_cool_air_max;
          break;
        case MODES.HEATING:
          maxTemp = this.isWater
            ? this.device.range_sp_hot_water_max
            : this.device.range_sp_hot_air_max;
          break;
        default:
          maxTemp = this.isWater
            ? Math.min(this.device.range_sp_cool_water_max, this.device.range_sp_hot_water_max)
            : Math.min(this.device.range_sp_cool_air_max, this.device.range_sp_hot_air_max);
      }

      return maxTemp;
    },

    /**
     * Devuelve el valor mínimo de temperatura del dispositivo
     *
     * @return {Number} - Temperatura mínima
     */
    minTemp() {
      if (this.ACS && this.ACSMode) return this.device.range_sp_acs_min;

      let minTemp = 0;

      switch (this.device.mode) {
        case MODES.AUTO:
          minTemp = this.isWater
            ? this.device.range_sp_auto_water_min
            : this.device.range_sp_auto_air_min;
          break;
        case MODES.COOLING:
          minTemp = this.isWater
            ? this.device.range_sp_cool_water_min
            : this.device.range_sp_cool_air_min;
          break;
        case MODES.HEATING:
          minTemp = this.isWater
            ? this.device.range_sp_hot_water_min
            : this.device.range_sp_hot_air_min;
          break;
        default:
          minTemp = this.isWater
            ? Math.min(this.device.range_sp_cool_water_max, this.device.range_sp_hot_water_max)
            : Math.min(this.device.range_sp_cool_air_max, this.device.range_sp_hot_air_max);
      }

      return minTemp;
    },
  },

  watch: {
    device(newValue, oldValue) {
      if (oldValue && !newValue) this.$router.push({ name: 'home' });
    },
  },

  methods: {
    /**
     * Enciende el dispositivo
     *
     * Si está bloqueada esta acción muestra un mensaje
     * Si el dispositivo tienen algún error o ya está encendido, no hace nada
     */
    switchOn() {
      if (this.device.block_on) {
        this.$toast.fail({
          duration: 1500,
          message: i18n.t('unit.block'),
          mask: true,
        });
        return;
      }

      if (this.fallback) {
        this.$toast.fail({
          duration: 2000,
          message: i18n.t('unit.fallback'),
          mask: true,
        });
        return;
      }

      if (this.deviceFail) return;

      if (this.ACSMode) {
        if (this.device.power_acs) return;
        this.setProperty('power_acs', true, 0);
      } else {
        if (this.device.power) return;
        this.setProperty('power', true, 0);
      }
    },

    /**
     * Apaga el dispositivo
     *
     * Si está bloqueada esta acción muestra un mensaje
     * Si el dispositivo tienen algún error o ya está apagado, no hace nada
     */
    swithOff() {
      if (this.device.block_off) {
        this.$toast.fail({
          duration: 1500,
          message: i18n.t('unit.block'),
          mask: true,
        });
        return;
      }

      if (this.fallback) {
        this.$toast.fail({
          duration: 2000,
          message: i18n.t('unit.fallback'),
          mask: true,
        });
        return;
      }

      if (this.deviceFail) return;

      if (this.ACSMode) {
        if (!this.device.power_acs) return;
        if (this.device.power_hot_water) {
          this.setProperty('power_hot_water', false, 0);
        }
        this.setProperty('power_acs', false, 0);
      } else {
        if (!this.device.power) return;
        this.setProperty('power', false, 0);
      }
    },

    /**
     * Alterna el modo máxima potencia cuando se trata de ACS
     */
    switchPowerful() {
      this.setProperty('power_hot_water', !this.device.power_hot_water);
    },

    /**
     * Envía un evento con el nuevo valor de la propiedad
     *
     * @param {String} property - Propiedad que se quiere modificar
     * @param {String} value - Valor de la propiedad
     */
    setProperty(property, value, delay) {
      try {
        console.log(`${property}:${value}`);
        this.DisableArcadeMixin(() => {
          const mac = this.mac;
          const data = { mac, property, value };
          SocketService.setDeviceValue(this.installationID, data);
        }, delay);
      } catch (error) {
        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      }
    },
  },
};

export default Unit;
